/* eslint-disable max-len */
import {
  Accordion, Button, DetailList, TabList, CLSPPlayer, Modal, VideoContainer,
  ActionModal,
} from '@skyline-platform/alchemy';
import {
  useEffect, useRef, useState,
} from 'react';
import '../Viewer.css';
import { tabEventEmitter } from '../utils/TabEventEmitters';
import { CameraForm } from '@components/FormComponents/CameraForm/CameraForm';
import { deleteDevice, getDevices } from '@utils/graphql/creators/devices';
import {
  checkDuplicateTab, checkSourceForDevice, removeTab, renderBadgeForStreamStatus, streamAddressHasChanged, updateNestedCameraObject, updateStreamOrder,
} from '../utils/helpers';
import { useMessageEmitter } from '@utils/react/eventEmitterContext';
import { useEffectOnce } from '@utils/react/hooks';
import { DeletionModal } from '@components/DeletionModal/DeletionModal';
import { EditModal } from '@components/EditModal/EditModal';
import { StreamStepperFlow } from '@components/FormComponents/StreamStepperFlow/StreamStepperFlow';
import PropTypes from 'prop-types';
import is from 'is';
import {
  DeleteButton, EditButton, EllipsisTrigger,
} from '../TableActions';
import { deleteSource } from '@utils/graphql/creators/sources';
import { SourceForm } from '@components/FormComponents/SourceForm/SourceForm';
import { AddNewStreamIcon } from './AddStreamIcon';
import { cameraDeleteWarning, sourceDeleteWarning } from '../utils/Constants';
import { StreamsTab } from './StreamsTab';
import { formatSourceData } from '@components/FormComponents/SourceForm/utils';
import {
  getLogger, camelToHeader, capitalizeKeys, capitalizeWord, headerToKebab, filterEmptyValues,
} from '@utils/general';
import { alphabetizeByKey } from '@utils/arrayHandler';
import { MenuItem, Divider } from '@mui/material';
import { handleTableActionsKeyDown } from '../utils/helpers';
import { getConfig } from '@utils/config';

const logger = getLogger('src.pages.split.cameras.CameraViewer');

export const CameraViewer = ({ onTabDataPopulate, userIsAdmin }) => {
  const [currentFocusedTabId, setCurrentFocusedTabId] = useState(null);
  const [tabData, setTabData] = useState([]);
  const [focusTabId, setFocusTabId] = useState(null);
  const tabDataRef = useRef(tabData);
  const [title, setTitle] = useState('');
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [streamPages, setStreamPages] = useState({});
  const currentCameraIdRef = useRef(null);

  const retrieveCameraById = async (cameraId) => {
    const { query, execute } = getDevices(cameraId, null, { includeSourceQuery: true, includePrivateMetadata: true });
    query.addReturnProperties(
      'id',
      'name',
      'description',
      'type',
      'connection_type',
      'network_address',
      'latitude',
      'longitude',
      'elevation',
      'problem_camera',
      'metadata',
      'username',
      'has_creds',

      'sources.id',
      'sources.name',
      'sources.description',
      'sources.category',
      'sources.type',
      'sources.sdp_input',
      'sources.appliance.name',
      'sources.device.name',
      'sources.path',
      'sources.port',
      'sources.uri',
      'sources.scheme',
      // needed for source delete
      'sources.streams.name',
      'sources.streams.id',

      'streams.id',
      'streams.name',
      'streams.description',
      'streams.online',
      'streams.clsps_address',
      'streams.source.id',
      'streams.source.name',
      'streams.zone.id',
      'streams.zone.name',
      'streams.appliance.id',
      'streams.appliance.name',
      'streams.appliance.public_address',
      'streams.appliance.management_address',

      // needed for Streams tab
      'streams.pull_on_request',
      'streams.use_interleaved_rtsp_transport'
    );

    try {
      const { error, data } = await execute();
      if (error) {
        logger.error(error);
        return null;
      }
      return data;
    } catch (e) {
      logger.error(e);
      return null;
    }
  };

  /**
   * Triggered by the on onClick on AllCameras table.
   * Gets the camera by id and checks to see if it is already an open tab
   * @param {*} eventObject - Contains the event information of the clicked item in the table
   */
  const handleAddTabs = async (eventObject) => {
    const cameraId = eventObject.detail;
    const newCamera = await retrieveCameraById(cameraId);

    if (newCamera) {
      checkDuplicateTab(newCamera, setTabData, setFocusTabId);
      currentCameraIdRef.current = cameraId;
      if (tabDataRef.current.length > 0) {
        // after one tab is added, focus on the first tab
        const firstTab = document.getElementById('tab-0');
        const tableNameTag = document.getElementById(`datacell-${cameraId}`);
        firstTab.click();
        tableNameTag.focus(); // stop focus from jumping over to tabs
        setCurrentFocusedTabId(cameraId);
      }
    }
  };

  // replace current camera record with updated one from database
  const refreshDevice = async (cameraToUpdateId) => {
    if (cameraToUpdateId) {
      const updatedDevice = await retrieveCameraById(cameraToUpdateId);
      if (updatedDevice) {
        setTabData((prevTabs) => {
          return prevTabs.map((device) => {
            if (device.id !== updatedDevice.id) {
              return device;
            }

            // Update the streams within the device
            const updatedStreams = device.streams.map((stream) => {
              const updatedStream = updatedDevice.streams.find((updatedStream) => updatedStream.id === stream.id);
              if (!updatedStream) {
                return stream;
              }
              // Clone the current stream data because all properties are read-only
              // Note: structuredClone is available on modern browsers as of 2022. If we are supporting
              // browsers from before this then a polyfill is available in the core-js package.
              const clone = structuredClone(updatedStream);
              // If the stream address has not changed
              if (!streamAddressHasChanged(stream, clone)) {
                clone.clsps_address = stream.clsps_address; // Reassign the clsp address to the original
              }
              return clone;
            });

            // Return the updated device with the updated streams and apply updateStreamOrder
            return updateStreamOrder({
              ...device,
              streams: updatedStreams,
            }, updatedDevice);
          });
        });
      }
    }
  };

  const handleUpdatingDevice = (updateMessage) => {
    if (tabDataRef.current.length === 0) {
      return; // Do nothing if there are currently no tabs
    }
    const payload = updateMessage?.detail?.payload;
    if (!payload) return;
    const {
      model = '',
      action = '',
      data: jsonData = '',
    } = payload;
    const data = JSON.parse(jsonData);

    let updatedTabData;
    let deviceRefreshed = false;

    // callback for updating tabData
    const updateCallback = (updatedTabData) => {
      setTabData(updatedTabData);
    };

    const propertyToUpdate = model.toLowerCase();
    const pluralProperty = `${propertyToUpdate}s`;

    switch (action.toLowerCase()) {
      case 'create':
        switch (propertyToUpdate) {
          case 'stream':
            if (tabDataRef.current.some((tab) =>
              tab.sources.some((source) => source.id === data.current.source_id)
            ) || checkSourceForDevice(data.current.source_id, tabDataRef)) {
              refreshDevice(currentCameraIdRef.current);
            }
            return;
          case 'source':
            // refresh the device that the new source was added to
            refreshDevice(data.current.device_id);
            return;
          case 'metadata':
            refreshDevice(data.current.model_id);
            return;
        }
        return;
      case 'remove':
        switch (propertyToUpdate) {
          case 'device':
            // delete device from tab array
            updatedTabData = tabDataRef.current.filter((tab) => {
              return String(tab.id) !== String(data.previous.id);
            });
            setTabData(updatedTabData);
            return;
          case 'source':
          case 'stream':
            updatedTabData = tabDataRef.current.map((tab) => {
              return {
                ...tab,
                [pluralProperty]: tab[pluralProperty].filter((item) => String(item.id) !== String(data.previous.id)),
              };
            });
            setTabData(updatedTabData);
            return;
          case 'metadata':
            refreshDevice(data.previous.model_id);
            return;
        }
        return;
      case 'update':
        switch (propertyToUpdate) {
          case 'device':
            refreshDevice(data.current.id);
            return;
          case 'metadata':
            refreshDevice(data.current.model_id);
            return;
          case 'stream':
            // Check if source_id has changed and refresh device for each existing source
            if (data.current.source_id !== data.previous.source_id) {
              tabDataRef.current.forEach((tab) => {
                if (tab.sources.some((source) => [data.current.source_id, data.previous.source_id].includes(source.id))) {
                  refreshDevice(tab.id);
                  deviceRefreshed = true;
                }
              });

              if (deviceRefreshed) {
                return; // Exit the switch case if refreshDevice was triggered
              }
            }
          // This needs to fallthrough in order update the stream data itself (ie. stream name)
          // eslint-disable-next-line no-fallthrough
          case 'appliance': {
            // Guard: only patch if a display field changed — not connection_count.
            // During a storm, hundreds of appliance events arrive per minute but
            // total_connection_count is not shown in the camera viewer.
            const APPLIANCE_DISPLAY_FIELDS = [
              'name',
              'management_address',
              'public_address',
            ];
            const hasRelevantChange = APPLIANCE_DISPLAY_FIELDS.some(
              (field) => data.current[field] !== data.previous[field]
            );
            if (!hasRelevantChange) return;
            updatedTabData = updateNestedCameraObject(propertyToUpdate, data, tabDataRef, updateCallback);
            return;
          }
          case 'zone':
            updatedTabData = updateNestedCameraObject(propertyToUpdate, data, tabDataRef, updateCallback);
            return;
          case 'source':
            // If the device attached to a source changes and the new and/or old device is open in a tab,
            // that device needs to be updated so the user sees the new stream profile
            tabDataRef.current.some((tab) => {
              if (data.previous.device_id || data.current.device_id) {
                if (tab.id === data.previous.device_id || tab.id === data.current.device_id) {
                  refreshDevice(tab.id);
                }
              } else {
                updatedTabData = updateNestedCameraObject(propertyToUpdate, data, tabDataRef, updateCallback);
              }
            });
            return;
        }
    }
  };

  // Handle expanding stream video
  const handleExpand = (index) => {
    setExpandedIndex(index);
  };

  // Handle collapsing stream video
  const handleCollapse = () => {
    setExpandedIndex(null);
  };

  /***** SUBSCRIPTIONS *****/
  const messageHandler = useMessageEmitter();
  useEffectOnce(() => {
    messageHandler.subscribe(handleUpdatingDevice);

    return () => {
      messageHandler.unsubscribe(handleUpdatingDevice);
    };
  });
  /***** END SUBSCRIPTIONS *****/

  // Handles receiving tabs from AllCameras component
  useEffect(() => {
    // Listen for updateTabs events
    tabEventEmitter.on('updateTabs', handleAddTabs);

    onTabDataPopulate();
    tabDataRef.current = tabData;

    // Clean up the event listener on component unmount
    return () => {
      tabEventEmitter.off('updateTabs', handleAddTabs);
    };
  }, [tabData]);

  /**
   * Used when deleting a tab in the tablist that comes before the one
   * currently focused/selected. It has to be in the use effect to maintain
   * focus on the tab after the rerender when tabData updates.
   */
  useEffect(() => {
    if (currentFocusedTabId) {
      const currentFocusedTab =
        document.getElementById(currentFocusedTabId)?.parentElement;
      if (currentFocusedTab) {
        currentFocusedTab.focus({ preventScroll: true });
        currentFocusedTab.click();
      }
    }
  }, [tabData, currentFocusedTabId]);

  useEffect(() => {
    if (focusTabId !== null) {
      const tabElement = document.getElementById(focusTabId)?.parentElement;
      if (tabElement) {
        tabElement.focus();
        tabElement.click();
      }
      setTimeout(() => setFocusTabId(null), 0); // Reset the focusTabId after handling
    }
  }, [focusTabId]);

  const maxStreams = 4;

  const editButtonRef = useRef(null);
  const deleteButtonRef = useRef(null);
  const buttonArray = [editButtonRef, deleteButtonRef];

  return (
    <>
      {tabData && tabData.length > 0 ? ( //don't render if no current tabs
        <TabList
          canClose
          handleClose={(index) => removeTab(index, tabData, setTabData, tabEventEmitter, setCurrentFocusedTabId)}
          pages={tabData.map((tab, index) => {
            const baseCameraDetails = {
              Name: tab.name,
              Description: tab.description,
              'Camera Type': tab?.type === 'Unknown'
                ? 'Other'
                : camelToHeader(tab.type),
              ...(getConfig().CAMERA_CONNECTION_TYPE_ENABLED === 'true' && tab.connection_type
                ? { 'Connection Type': tab.connection_type }
                : {}),
              'Network Address': tab.network_address,
              Latitude: tab.latitude,
              Longitude: tab.longitude,
              Elevation: tab.elevation,
              'Problem Camera/Device': tab.problem_camera ? 'Yes' : 'No',
            };

            const metadataEntries = Array.isArray(tab.metadata)
              ? tab.metadata
                .filter(({ value }) => value !== null && value !== undefined && value !== '')
                .map(({
                  key, value, private: isPrivate,
                }) => [
                  isPrivate
                    ? <span data-single-word={!capitalizeWord(key).includes(' ')}>
                      <i className='fa-solid fa-key' aria-hidden='true' title='Private Metadata' />{' '}
                      <span title={capitalizeWord(key)}>{capitalizeWord(key)}</span>
                    </span>
                    : capitalizeWord(key),
                  value,
                ])
              : tab.metadata
                ? Object.entries(capitalizeKeys(JSON.parse(tab.metadata)))
                : [];

            const cameraDetails = [...Object.entries(filterEmptyValues(baseCameraDetails)), ...metadataEntries];

            let accordionInfo = [];
            const alphabetizedSources = alphabetizeByKey(tab.sources ?? [], 'name');

            alphabetizedSources.forEach((source) => {
              let sourceInfo = {
                Name: source.name,
                Description: source.description,
                Category: source.category,
                ...(!is.empty(source.scheme) && !is.null(source.scheme) && {
                  Protocol: source.scheme,
                  Path: source.path,
                  Port: source.port,
                }),
                ...(!is.empty(source.sdp_input) && !is.null(source.sdp_input) && {
                  'SDP Input': source.sdp_input,
                }),
              };
              accordionInfo.push({
                trigger:
                  <>
                    <span className='full-width-flex'>
                      {source.name}
                    </span>
                    {userIsAdmin && (
                      <>
                        <ActionModal
                          className='source-profile-actions'
                          id={headerToKebab(source.name)}
                          content={[
                            <MenuItem
                              key={`edit-${source.id}`}
                              onKeyDown={(event) => {
                                handleTableActionsKeyDown(event, buttonArray, 0);
                              }}
                            >
                              <EditModal
                                data={formatSourceData(source, tab)}
                                FormComponent={SourceForm}
                                setTitle={setTitle}
                                fieldName='streamProfile'
                                trigger={({ handleOpen }) =>
                                  <EditButton
                                    handleOpen={handleOpen}
                                    data={source}
                                    ref={editButtonRef}
                                  />
                                }
                              />

                            </MenuItem>,
                            <Divider key='divider' />,
                            <MenuItem
                              key={`delete-${source.id}`}
                              onKeyDown={(event) => {
                                handleTableActionsKeyDown(event, buttonArray, 1);
                              }}
                            >
                              <DeletionModal
                                customMessage={sourceDeleteWarning}
                                headerIcon='fa-solid fa-layer-group'
                                data={source}
                                handleDelete={deleteSource}
                                fieldName='streamProfile'
                                trigger={({ handleOpen }) =>
                                  <DeleteButton
                                    handleOpen={handleOpen}
                                    data={source}
                                    ref={deleteButtonRef}
                                  />
                                }
                              />
                            </MenuItem>,
                          ]}
                          menuStyle='icon-button'
                          menuPlacement='bottom-end'
                          summaryText={<EllipsisTrigger />}
                        />
                      </>
                    )}
                  </>,
                panel:
                  <>
                    <DetailList keyValuePair={filterEmptyValues(sourceInfo)} />
                  </>,
              });
            });

            const streams = tab.streams ?? [];
            const totalPages = Math.max(1, Math.ceil(streams.length / maxStreams));
            const page = Math.min(streamPages[tab.id] || 0, totalPages - 1);
            const pageStreams = streams.slice(page * maxStreams, (page + 1) * maxStreams);
            const handlePageChange = (newPage) => {
              setStreamPages((prev) => ({ ...prev, [tab.id]: newPage }));
              setExpandedIndex(null);
            };

            return (
              <>
                <div className='video-grid-wrapper'>
                  {totalPages > 1 && (
                    <button
                      className='video-grid-nav prev'
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 0}
                      aria-label='Previous page'
                    >
                      <i className='fa-solid fa-chevron-left' aria-hidden='true' />
                    </button>
                  )}
                  <div className="video-grid">
                    {pageStreams.map((stream, index) => (
                      <div
                        key={index}
                        className={`grid-item ${expandedIndex === index ? 'expanded' : ''}`}
                      >
                        <VideoContainer
                          action={expandedIndex === index ? 'Collapse Video' : 'Expand Video'}
                          actionIcon={expandedIndex === index ? 'fa-solid fa-minimize' : 'fa-solid fa-maximize'}
                          containerColor='var(--vp-skyline-blue)'
                          badge={renderBadgeForStreamStatus(stream.online)}
                          handleAction={() => (expandedIndex === index ? handleCollapse() : handleExpand(index))}
                          title={
                            `Name: ${stream.name} | Zone: ${stream.zone?.name} | Address: ${stream.appliance?.public_address ||
                            stream.appliance?.management_address
                            }`}
                          key={stream.id}
                        >
                          <CLSPPlayer
                            title={stream.name}
                            type='clsp'
                            src={stream.clsps_address}
                            key={stream.id}
                          />
                        </VideoContainer>
                      </div>
                    ))}
                    {pageStreams.length < maxStreams &&
                      Array.from({ length: maxStreams - pageStreams.length }).map((_, index) => (
                        <div key={`add-button-${index}`} className="grid-item">
                          <Modal
                            className='new-stream-modal'
                            closeIcon
                            header={title}
                            trigger={({ handleOpen }) => (
                              <Button
                                label='Add New Stream'
                                state={index !== 0 || !userIsAdmin ? 'disabled' : 'default'} // Disable all buttons except the first
                                type='text'
                                onClick={handleOpen}
                              >
                                <AddNewStreamIcon />
                              </Button>
                            )}
                            slots={{
                              content: (handleClose) => (
                                <StreamStepperFlow
                                  handleClose={handleClose}
                                  setTitle={setTitle}
                                  lockDownFields={{ device: { id: tab.id, name: tab.name } }}
                                />
                              ),
                            }}
                          />
                        </div>
                      ))}
                  </div>
                  {totalPages > 1 && (
                    <button
                      className='video-grid-nav next'
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages - 1}
                      aria-label='Next page'
                    >
                      <i className='fa-solid fa-chevron-right' aria-hidden='true' />
                    </button>
                  )}
                </div>
                <TabList
                  pages={[
                    <div key={'detail' + index}>
                      {/* showing scrollbars */}
                      <DetailList keyValuePair={cameraDetails} tabIndex={0} />
                      {userIsAdmin && (
                        <>
                          <div className='button-container'>
                            <EditModal
                              data={tab}
                              fieldName='camera'
                              FormComponent={CameraForm}
                              trigger={({ handleOpen }) => (
                                <Button
                                  key={'edit-button' + index}
                                  label='Edit Camera Details'
                                  type='filled'
                                  onClick={handleOpen}
                                />
                              )}
                            />
                            <DeletionModal
                              customMessage={cameraDeleteWarning}
                              headerIcon='fa-solid fa-video'
                              data={tab}
                              fieldName='camera'
                              handleDelete={deleteDevice}
                              trigger={({ handleOpen }) => (
                                <Button
                                  key={'delete-button' + index}
                                  label='Delete Camera'
                                  state='error'
                                  type='filled'
                                  onClick={handleOpen}
                                />
                              )}
                            />
                          </div>
                        </>
                      )}
                    </div>,
                    <div key={'source' + index}>
                      <Accordion iconLeft='fa-solid fa-chevron-down' accordionData={accordionInfo} id={tab.name} />
                    </div>,
                    <div key={'streams' + index}>
                      {tab.streams?.length
                        ? <StreamsTab streamData={tab.streams} tableKey={tab.id} userIsAdmin={userIsAdmin} />
                        : <></>
                      }
                    </div>,
                  ]}
                  tabs={[
                    'Details',
                    'Stream Profiles',
                    'Streams',
                  ]}
                />
              </>
            );
          })}
          tabs={tabData.map((tab, index) => (
            <span
              title={tab.name}
              key={index}
              id={tab.id}
              tabIndex={-1}
              onClick={() => {
                tabEventEmitter.emit('selectTab', tab.id); //emits event to AllCameras
                // This is needed to keep the correct tab focused when making an edit to data in that tab
                setCurrentFocusedTabId(tab.id);
              }}
            >
              {tab.name}
            </span>
          ))}
        />
      ) : null}
    </>
  );
};

CameraViewer.propTypes = {
  onTabDataPopulate: PropTypes.func.isRequired,
  userIsAdmin: PropTypes.bool.isRequired,
};
